import * as React from 'react';
import {
  ACTION_BUSINESS_AVAILABILITY_SEARCH,
  CATEGORY_BUSINESS_PAGE,
} from 'utils/analytics/eventTypesConstants';
import {
  ACTION_BUSINESS_CLICK_RESERVATION_SEARCH,
  ACTION_BUSINESS_VIEW_CALENDAR_DATE,
  ACTION_SEARCH_BUSINESS_CALENDAR,
} from 'utils/analytics/eventTypeConstantsAmplitude';
import { Button } from 'omakase';
import { LocalDate, LocalTime } from 'js-joda';
import { ReservationSearchBarCard } from 'components/business/landing-page/ReservationSearchBar/ReservationSearchBarCard';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { SearchSizeType, computeInitialSearchState } from 'utils/searchBar';
import { allDates, computeDisplayedTimes, getOpenDates } from 'utils/offerings';
import { allowsPartySizeSelection, offeringPartyItem } from '@tock/shared/utils/offerings';
import { consumer, core } from '@tock/coretypes';
import { experienceToData } from 'utils/analytics/amplitude';
import { joinPath } from 'utils/pathUtils';
import { useAmplitudeSendEvent } from 'utils/analytics/AnalyticsContext';
import { useConsumerConversionAnalytics } from 'hooks/analytics/useConsumerConversionAnalytics';
import { useDidUpdateEffect, useMountEffect, usePrevious } from '@tock/hooks';
import Anchor from 'components/Anchor';
import ButtonGroup from '@tock/shared/components/buttons/ButtonGroup';
import ConsumerCalendar from 'components/calendar/ConsumerCalendar';
import SearchBarDateSection from 'components/business/search-bar/SearchBarDateSection';
import SearchBarGuestSection from 'components/business/search-bar/SearchBarGuestSection';
import SearchBarTimeSection from 'components/business/search-bar/SearchBarTimeSection';
import _ from 'lodash';
import clsx from 'clsx';
import queryString from '@tock/query-string';

interface Props {
  availableDates?: DateMap;
  business?: consumer.IBusinessConfig;
  date?: string;
  experienceInfo?: core.ITicketType;
  hasDisconnectedSearchButton?: boolean;
  hasExpandedCalendar?: boolean;
  hasInitialFocus?: boolean;
  hasNoTimeField?: boolean;
  isInstant?: boolean;
  lockedDates?: DateMap;
  offeredDates?: DateMap;
  offerings?: consumer.ConsumerOfferings;
  onAdditionalCallback?(): void;
  onSearch(
    size: number,
    date: string | null,
    time: string | null,
    seatingAreaId: number | null | undefined
  ): void;
  size?: number;
  seatingAreaId?: number;
  canChangePartySize?: boolean;
  searchString?: string;
  showBusinessCard?: boolean;
  soldDates?: DateMap;
  ticketTypeId?: number;
  time?: string;
  searchSizeType: SearchSizeType;
}

interface MatchParams {
  url: string;
}

const ReservationSearchBar: React.FC<Props & RouteComponentProps<MatchParams>> = ({
  availableDates,
  business,
  canChangePartySize = true,
  date,
  experienceInfo,
  hasDisconnectedSearchButton = false,
  hasExpandedCalendar = false,
  hasInitialFocus = false,
  hasNoTimeField = false,
  isInstant = false,
  lockedDates,
  match: { url },
  offeredDates,
  offerings,
  onAdditionalCallback,
  onSearch,
  searchSizeType,
  searchString,
  showBusinessCard,
  seatingAreaId,
  size,
  soldDates,
  ticketTypeId,
  time,
}) => {
  const amplitudeSendEvent = useAmplitudeSendEvent();
  const initialSearchStateValues = React.useMemo(
    () =>
      computeInitialSearchState(
        availableDates,
        offeredDates,
        business,
        offerings,
        size,
        date,
        time,
        searchString,
        hasExpandedCalendar,
        ticketTypeId,
        searchSizeType
      ),
    [
      availableDates,
      offeredDates,
      business,
      offerings,
      size,
      date,
      time,
      searchString,
      hasExpandedCalendar,
      ticketTypeId,
      searchSizeType,
    ]
  );

  const [minSize] = React.useState(initialSearchStateValues.minSize);
  const [maxSize] = React.useState(initialSearchStateValues.maxSize);
  const [selectedDate, setSelectedDate] = React.useState(initialSearchStateValues.selectedDate);
  const [selectedSize, setSelectedSize] = React.useState(initialSearchStateValues.selectedSize);
  const [selectedTime, setSelectedTime] = React.useState(initialSearchStateValues.selectedTime);
  const [selectedSeatingArea, setSelectedSeatingArea] = React.useState<number | undefined>(
    seatingAreaId
  );
  const [showDateSelector] = React.useState(initialSearchStateValues.showDateSelector);
  const [showExpandedCalendar, setShowExpandedCalendar] = React.useState(
    initialSearchStateValues.showExpandedCalendar
  );
  const [showGuestSelector] = React.useState(initialSearchStateValues.showGuestSelector);
  const [showTimeSelector] = React.useState(initialSearchStateValues.showTimeSelector);
  const sendConversionAnalytics = useConsumerConversionAnalytics();

  // Wrapped in useCallback due to being used as a dependency for other hooks
  const handleSearch = React.useCallback(() => {
    onSearch(
      selectedSize,
      selectedDate ? selectedDate.toString() : null,
      selectedTime ? selectedTime.toString() : null,
      selectedSeatingArea
    );
  }, [onSearch, selectedDate, selectedSize, selectedTime, selectedSeatingArea]);

  useMountEffect(() => {
    if (
      isInstant &&
      (selectedSize !== size ||
        selectedDate?.toString() !== date ||
        selectedTime?.toString() !== time)
    ) {
      handleSearch();
    }
  });

  const prevDate = usePrevious(date);
  React.useEffect(() => {
    if (date && date !== prevDate) {
      setSelectedDate(LocalDate.parse(date));
    }
  }, [date, prevDate]);

  const onSearchCallback = React.useCallback(
    () => (isInstant ? handleSearch : undefined),
    [isInstant, handleSearch]
  );

  const prevState = usePrevious({
    selectedDate,
    selectedTime,
    selectedSize,
    selectedSeatingArea,
    seatingAreaId,
  });

  useDidUpdateEffect(() => {
    if (prevState?.seatingAreaId === seatingAreaId) {
      return;
    }
    setSelectedSeatingArea(seatingAreaId);
  });

  // Updates search based on search params values from state (selectedDate, selectedTime, selectedSize).
  // Because updates to selectedDate can come from outside the component, we compare the
  // date prop & selectedDate before making an update triggered by a change in selectedDate
  useDidUpdateEffect(() => {
    const callback = onSearchCallback();
    const requireTimeUpdate = !prevState?.selectedTime?.equals(selectedTime);
    const requireSizeUpdate = prevState?.selectedSize !== selectedSize;
    const requireDateUpdate =
      !prevState?.selectedDate?.equals(selectedDate) &&
      !(date && LocalDate.parse(date).equals(selectedDate));
    const seatingAreaUpdate = prevState?.selectedSeatingArea !== selectedSeatingArea;

    if (requireTimeUpdate || requireSizeUpdate || requireDateUpdate || seatingAreaUpdate) {
      callback?.();
    }
  }, [
    date,
    onSearchCallback,
    prevState?.selectedDate,
    prevState?.selectedTime,
    prevState?.selectedSize,
    prevState?.selectedSeatingArea,
    selectedDate,
    selectedSize,
    selectedTime,
    selectedSeatingArea,
  ]);

  const disconnectedSearch = () => {
    handleSearch();
    if (onAdditionalCallback) {
      onAdditionalCallback();
    }
  };

  const handleDecrementGuests = () => {
    setSelectedSize((selectedSize || 1) - 1);
  };

  const handleIncrementGuests = () => {
    setSelectedSize((selectedSize || 1) + 1);
  };

  const handleToggleExpandedCalendar = (event: React.MouseEvent<HTMLElement>) => {
    if (showExpandedCalendar) {
      handleHideExpandedCalendar();
    } else {
      setShowExpandedCalendar(!showExpandedCalendar);
    }
  };

  const handleHideExpandedCalendar = () => {
    setShowExpandedCalendar(false);
  };

  const handleSelectDate = (dateInternal: string) => {
    setSelectedDate(LocalDate.parse(dateInternal));
    amplitudeSendEvent(ACTION_BUSINESS_VIEW_CALENDAR_DATE, {
      partySize: size,
      date: date,
      time: time,
      ...experienceToData(experienceInfo),
    });
  };

  const handleSelectTime = (timeInternal: string) => {
    const selectedTimeInternal = LocalTime.parse(timeInternal);
    setSelectedTime(selectedTimeInternal);
  };

  const displayedTimes = computeDisplayedTimes(business, offerings, LocalDate.now().toString());

  const dates = offerings ? getOpenDates(offerings) : allDates();
  const availableDatesOrDefault = availableDates ? availableDates : dates;
  const lockedDatesOrDefault = lockedDates ? lockedDates : {};
  const offeredDatesOrDefault = offeredDates ? offeredDates : dates;
  const soldDatesOrDefault = soldDates ? soldDates : {};

  const experience = _.find(offerings?.experience, (experience) => experience.id === ticketTypeId);
  const showSizeSelector =
    !experience?.type ||
    allowsPartySizeSelection(experience.type, experience.descriptiveVariety ?? undefined);

  const sizeClassName = clsx('SearchBar-section', 'SearchBar-section--left', {
    'is-filled': !!selectedSize,
    'is-open': showGuestSelector,
  });

  const dateClassName = clsx('SearchBar-section', 'Dropdown-section', 'Dropdown-calendar', {
    'SearchBar-section--left': !showSizeSelector,
    'Dropdown-middleChild': showSizeSelector,
    'is-filled': !!selectedDate,
    'is-open': showDateSelector || showExpandedCalendar,
    'is-not-visible-on-mobile': !hasNoTimeField,
  });

  const timeClassName = clsx('SearchBar-section', 'Dropdown-section', {
    'is-filled': !!selectedTime,
    'is-open': showTimeSelector,
    'Dropdown-middleRightChild': !isInstant,
    'Dropdown-rightChild': isInstant,
  });

  const initiallyFocusGuestSection =
    hasInitialFocus && showSizeSelector && (selectedSize < maxSize || selectedSize > minSize);
  const initiallyFocusDateSection = hasInitialFocus && !initiallyFocusGuestSection;

  const businessName = business?.name;
  const ariaLabel =
    (businessName ? `Search within ${businessName}` : 'Search') + ' (opens a dialog)';

  return (
    <div>
      <div className="SearchBar" data-testid="reservation-search-bar">
        {business && (
          <ReservationSearchBarCard
            showBusinessCard={showBusinessCard}
            business={business as consumer.BusinessConfig}
          />
        )}
        {showSizeSelector && (
          <SearchBarGuestSection
            disabled={!canChangePartySize}
            handleDecrementGuests={handleDecrementGuests}
            handleIncrementGuests={handleIncrementGuests}
            maxSize={maxSize}
            minSize={minSize}
            selectedSize={selectedSize}
            sizeClassName={sizeClassName}
            partySizeUnit={
              experience?.type
                ? offeringPartyItem(experience.type, experience?.descriptiveVariety ?? undefined)
                : undefined
            }
            hasInitialFocus={initiallyFocusGuestSection}
          />
        )}

        <SearchBarDateSection
          availableDates={availableDatesOrDefault}
          dateClassName={dateClassName}
          hasExpandedCalendar={hasExpandedCalendar}
          lockedDates={lockedDatesOrDefault}
          offeredDates={offeredDatesOrDefault}
          onSelectDate={handleSelectDate}
          onToggleExpandedCalendar={handleToggleExpandedCalendar}
          openDates={dates}
          selectedDate={selectedDate}
          soldDates={soldDatesOrDefault}
          hasInitialFocus={initiallyFocusDateSection}
        />

        {!hasNoTimeField && (
          <SearchBarTimeSection
            displayedTimes={displayedTimes}
            onSelectTime={handleSelectTime}
            selectedTime={selectedTime}
            timeClassName={timeClassName}
          />
        )}

        {!isInstant && (
          <Button
            href={`${joinPath(url, `/search`)}?${queryString.stringify({
              size: selectedSize,
              date: selectedDate ? selectedDate.toString() : null,
              time: selectedTime ? selectedTime.toString() : null,
            })}`}
            component={Anchor}
            aria-label={ariaLabel}
            data-testid="reservation-search-submit"
            size="medium"
            variant="primary"
            fullWidth
            onClick={() => {
              window.analytics.sendEvent(
                CATEGORY_BUSINESS_PAGE,
                ACTION_BUSINESS_AVAILABILITY_SEARCH
              );

              amplitudeSendEvent(ACTION_SEARCH_BUSINESS_CALENDAR, {
                partySize: size,
                date: date,
                time: time,
              });

              sendConversionAnalytics(ACTION_BUSINESS_CLICK_RESERVATION_SEARCH);
            }}
          >
            Search
          </Button>
        )}
      </div>

      {hasExpandedCalendar && showExpandedCalendar && (
        <div className="SearchBarMonths">
          <ConsumerCalendar
            availableDates={availableDatesOrDefault}
            lockedDates={lockedDatesOrDefault}
            offeredDates={offeredDatesOrDefault}
            onSelect={handleSelectDate}
            openDates={dates}
            selectedDate={selectedDate ? selectedDate.toString() : undefined}
            soldDates={soldDatesOrDefault}
            suppressAutoFocus
            wideView
          />
        </div>
      )}

      {hasDisconnectedSearchButton && (
        <ButtonGroup>
          <Button variant="primary" fullWidth size="medium" onClick={disconnectedSearch}>
            Search
          </Button>
        </ButtonGroup>
      )}
    </div>
  );
};

export default withRouter(ReservationSearchBar);
